"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Badge } from "../../components/ui/badge"
import { Card, CardContent } from "../../components/ui/card"
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import {
  Search,
  Filter,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  Mail,
  Building,
  User,
  MapPin,
  Phone,
  UserCheck,
  GraduationCap,
  Languages,
  Briefcase,
  Award,
  CreditCard,
  Baby,
  CheckCircle,
  ChevronDown,
  X,
  ArrowLeft,
} from "lucide-react"
import {
  getCandidateById,
  updateClaimStatus,
  updateVerificationStatus,
  updateTenureStatus,
  updateTenureEligibilityStatus,
  TenureEligibilityStatus,
} from "../../services/candidateapi"
import { toast } from "react-hot-toast"
import { getJobs } from "../../services/jobsapi"
import { useRbac } from "../../context"
import { getMySubordinates } from "../../services/usersapi"
import { datePickerStyles } from "../../components/constants/styles"
import { getStatusBadgeColor, showConfirmation } from "../../components/utils/application.utils"
import ExcelDownloadButton from "../../components/exceldownloadforjoinedcandidates"
import type { Application, PaginationState, UserType } from "../../components/types/application.types"

// Import the new API client
import { getJoinedApplications } from "../../services/candidateapi"
import { cn } from "../../components/lib/utils"
import { Textarea } from "../../components/ui/textarea"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"

interface Candidate {
  id: string
  name: string
  email: string
  phone: string
  highestQualification: string
  totalExperienceYears: number
  totalExperienceMonths: number
  skills: string[]
  currentLocationDetails: {
    city: string
    fullAddress: string
  }
  profileSource: string
  createdAt: string
  candidateProfileStrength: number
  fatherName?: string
  gender?: string
  dateOfBirth?: string
  aadharNumber?: string
  jobCategory?: string
  employmentType?: string
  spokenLanguages?: string[]
  preferredJobCategories?: string[]
  preferredEmploymentTypes?: string[]
}

interface JobDetails {
  id: string
  title: string
  companyName: string
  salary?: string
  commissionReceived?: string
  commissionPayable?: string
  commissionPaid?: string
}

interface ApplicationWithTenure {
  applicationId: string
  candidateName: string
  email: string
  phone: string
  employeeId: string | null
  companyName: string
  companyLocation: string
  position: string | null
  tenureInDays: number
  salary: number | null
  commissionPayable: number | null
  isCommissionClaimed: boolean
  joiningDate: string // ISO 8601 date string
  ledBy: {
    id: string
    username: string
  }
  verificationStatus: string
  status: string
  tenure: {
    status: string
    daysLeft: number | string | null
    isApproved: boolean
  }
}


export function ViewTenureApplications() {
  const navigate = useNavigate()
  const { user } = useRbac()
  const isAdmin = (user?.role as string) === "ADMIN" || (user?.role as string) === "SHM"

  useEffect(() => {
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style")
      styleSheet.innerText = datePickerStyles
      document.head.appendChild(styleSheet)
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet)
        }
      }
    }
  }, [])

  const [applications, setApplications] = useState<ApplicationWithTenure[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  })
  const [showCandidateDetailsModal, setShowCandidateDetailsModal] = useState(false)
  const [selectedCandidateForDetails, setSelectedCandidateForDetails] = useState<Candidate | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)
  const [jobs, setJobs] = useState<JobDetails[]>([])
  const [recruiters, setRecruiters] = useState<UserType[]>([])
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false)
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [searchInputValue, setSearchInputValue] = useState<string>("")
  const [selectedJob, setSelectedJob] = useState<string>("all")
  const [selectedRecruiter, setSelectedRecruiter] = useState<string>("all")
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("")
  const [selectedDateTo, setSelectedDateTo] = useState<string>("")
  const [showEmploymentModal, setShowEmploymentModal] = useState(false)
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>("")
  const [employmentForm, setEmploymentForm] = useState({
    employeeId: "",
    positionName: "",
    salary: "",
    commission: "",
    commissionPaid: "",
  })
  const [showClaimModal, setShowClaimModal] = useState(false)
  const [claimComment, setClaimComment] = useState("")
  const [claimingAppId, setClaimingAppId] = useState<string>("")
  const isFetchingRef = useRef(false)

  // Add new state variables for additional filters
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState<string>("all")
  const [selectedTenureStatus, setSelectedTenureStatus] = useState<string>("all")
  const [selectedTenureEligibilityStatus, setSelectedTenureEligibilityStatus] = useState<string>("all")
  const [selectedClaimStatus, setSelectedClaimStatus] = useState<string>("all")

  // Define tenureStatusOptions
  const tenureStatusOptions: SearchableSelectOption[] = [
    { value: "all", label: "All Tenure Status" },
    { value: "Approved", label: "Approved" },
    { value: "Not Approved", label: "Not Approved" },
    { value: "Pending", label: "Pending" },
    // { value: "Rejected", label: "Rejected" },
  ]

  // Define tenureEligibilityStatusOptions
  const tenureEligibilityStatusOptions: SearchableSelectOption[] = [
    { value: "all", label: "All Eligibility Status" },
    { value: TenureEligibilityStatus.UNKNOWN, label: "Unknown" },
    { value: TenureEligibilityStatus.ELIGIBLE, label: "Eligible" },
    { value: TenureEligibilityStatus.NOT_ELIGIBLE, label: "Not Eligible" },
  ]

  // Define claimStatusOptions
  const claimStatusOptions: SearchableSelectOption[] = [
    { value: "all", label: "All Claim Status" },
    { value: "true", label: "Claimed" },
    { value: "false", label: "Not Claimed" },
  ]

  const jobOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Jobs" }]
    if (jobs.length > 0) {
      jobs.forEach((job) => {
        options.push({ value: job.title, label: job.title })
      })
    } else if (!isLoadingJobs) {
      options.push({ value: "no-jobs", label: "No jobs available", disabled: true })
    }
    return options
  }, [jobs, isLoadingJobs])

  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }]
    if (recruiters.length > 0) {
      recruiters.forEach((recruiter) => {
        options.push({ value: recruiter.username, label: recruiter.username })
      })
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true })
    }
    return options
  }, [recruiters, isLoadingRecruiters])



  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value)

      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }

      const newTimer = setTimeout(() => {
        setSearchTerm(value)
      }, 500)

      setSearchDebounceTimer(newTimer)
    },
    [searchDebounceTimer],
  )

  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }
    }
  }, [searchDebounceTimer])

  // Update the fetchCandidates function to include all filters
  const fetchCandidates = useCallback(async () => {
    if (isFetchingRef.current) {
      return
    }
    isFetchingRef.current = true
    setIsLoading(true)
    setError(null)
    try {
      // Only add filters if they are not "all" or empty
      const params: any = {
        page: pagination.page,
        limit: 10,
        _t: Date.now(),
      }
      if (searchTerm) params.search = searchTerm
      if (selectedJob !== "all") params.jobId = selectedJob
      if (selectedRecruiter !== "all") params.recruiterId = selectedRecruiter
      if (selectedVerificationStatus !== "all") params.verificationStatus = selectedVerificationStatus
      if (selectedTenureStatus !== "all") params.tenureStatus = selectedTenureStatus
      if (selectedTenureEligibilityStatus !== "all") params.tenureEligibilityStatus = selectedTenureEligibilityStatus
      if (selectedClaimStatus !== "all") params.claimStatus = selectedClaimStatus
      if (selectedDateFrom) params.joiningDateFrom = selectedDateFrom
      if (selectedDateTo) params.joiningDateTo = selectedDateTo

      const response = await getJoinedApplications(params)

      const apps = (response.data.data.applications ?? []).map((app: any) => {
        const isClaimed = !!app.isClaimed
        const commPayable = Number(app.commissionPayable ?? app.job?.commissionPayable ?? 0)
        return {
          ...app,
          candidate: {
            id: app.candidateId ?? "",
            name: app.candidateName ?? "N/A",
            currentLocationDetails: { city: app.companyLocation ?? "N/A" },
            createdAt: app.joiningDate ?? null,
          },
          job: {
            id: app.jobId ?? "",
            title: app.position ?? "N/A",
            companyName: app.companyName ?? "N/A",
            salary: app.salary ?? "N/A",
            isCommissionClaimed: app.isCommissionClaimed ?? "N/A",
            commissionPayable: app.commissionPayable ?? "0",
          },
          commClaimed: isClaimed ? commPayable : 0,
          isClaimed,
          tenureDaysLeft: app.tenureDaysLeft ?? Math.floor(Math.random() * 30),
          tenureStatus: app.tenure.status ?? "Unknown",
          verificationStatus:
            app.verificationStatus ?? ["Pending", "Verified", "Rejected"][Math.floor(Math.random() * 3)],
          tenureEligibilityStatus: app.tenureEligibilityStatus ?? "Unknown",
          employeeId: app.employeeId ?? "",
          joiningDate: app.joiningDate ?? "",
          ledBy: app.ledBy ?? { id: "", username: "N/A" },
        }
      })

      setApplications(apps)
      setPagination((prev) => ({
        ...prev,
        totalPages: response.data?.totalPages || Math.ceil((response.data?.totalResults ?? 0) / pagination.limit) || 1,
        totalResults: response.data?.totalResults || apps.length,
      }))
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError("Authentication failed. Please log in again.")
        toast.error("Authentication failed. Please log in again.")
      } else if (err.response?.status === 403) {
        setError("Access denied. You may not have permission to view joined applications.")
        toast.error("Access denied. You may not have permission to view joined applications.")
      } else if (err.response?.status === 404) {
        setError("No joined applications found.")
        toast.error("No joined applications found.")
      } else {
        setError(err.response?.data?.message || "Failed to fetch joined applications.")
        toast.error(err.response?.data?.message || "Failed to fetch joined applications.")
      }
    } finally {
      setIsLoading(false)
      isFetchingRef.current = false
    }
  }, [pagination.page, pagination.limit, searchTerm, selectedJob, selectedRecruiter, selectedVerificationStatus, selectedTenureStatus, selectedTenureEligibilityStatus, selectedClaimStatus, selectedDateFrom, selectedDateTo])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }))
    fetchCandidates()
  }, [pagination.limit, searchTerm, selectedJob, selectedRecruiter, selectedVerificationStatus, selectedTenureStatus, selectedTenureEligibilityStatus, selectedClaimStatus, selectedDateFrom, selectedDateTo])

  useEffect(() => {
    if (pagination.page > 1) {
      fetchCandidates()
    }
  }, [pagination.page])

  useEffect(() => {
    fetchJobs()
    fetchRecruiters()
  }, [])

  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true)
    try {
      const response = await getJobs({
        page: 1,
        limit: 100,
        _t: Date.now(),
      })

      if (response?.data?.jobs) {
        setJobs(
          response.data.jobs.map((job: any) => ({
            id: job.id,
            title: job.title,
            companyName: job.companyName,
            salary: job.salary,
            commissionReceived: job.commissionReceived,
            commissionPayable: job.commissionPayable,
            commissionPaid: job.commissionPaid,
          })),
        )
      } else {
        setJobs([])
        // toast.error("No jobs found in the system.", {
        //   duration: 3000,
        // })
      }
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err)
      setJobs([])
      toast.error("Failed to load jobs for filtering")
    } finally {
      setIsLoadingJobs(false)
    }
  }, [])

  const fetchRecruiters = useCallback(async () => {
    setIsLoadingRecruiters(true)
    try {
      const response = await getMySubordinates()

      if (response?.data?.subordinates) {
        setRecruiters(response.data.subordinates)
      } else {
        setRecruiters([])
        // toast.error("No recruiters found in the system.", {
        //   duration: 3000,
        // })
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err)
      setRecruiters([])
      toast.error("Failed to load recruiters for filtering")
    } finally {
      setIsLoadingRecruiters(false)
    }
  }, [])

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }))
    }
  }

 
  const handleTenureStatusChange = async (applicationId: string, value: string) => {
    if (value === "Approved") {
      setSelectedApplicationId(applicationId)
      setShowEmploymentModal(true)
      return
    }

    let status: boolean | undefined
    if (value === "Rejected") status = false
    else status = undefined

    await updateTenureStatus(applicationId, { isTenureApproved: !!status })
    setApplications((apps) =>
      apps.map((app) => (app.applicationId === applicationId ? { ...app, tenureStatus: status } : app)),
    )
  }

  const handleUpdateVerificationStatus = async (applicationId: string, newStatus: string) => {
    try {
      await updateVerificationStatus(applicationId, { verificationStatus: newStatus })
      fetchCandidates()
    } catch (error) {
      console.error("Failed to update verification status:", error)
    }
  }

  const handleUpdateTenureEligibilityStatus = async (applicationId: string, status: TenureEligibilityStatus) => {
    try {
      await updateTenureEligibilityStatus(applicationId, status)
      // Refresh the applications list to reflect the updated status
      fetchCandidates()
    } catch (error) {
      console.error("Failed to update tenure eligibility status:", error)
      // Error handling is already done in the API function with toast notifications
    }
  }

  const handleEmploymentSubmit = async () => {
    try {
      const payoutDetails = {
        employeeIdAssignedByCompany: employmentForm.employeeId,
        finalPositionName: employmentForm.positionName,
        finalSalary: Number(employmentForm.salary),
        commissionReceivableFromCompany: Number(employmentForm.commission),
        commissionPayableToSourcer: Number(employmentForm.commissionPaid),
      }

      await updateTenureStatus(selectedApplicationId, {
        isTenureApproved: true,
        payoutDetails,
      })

      setApplications((apps) =>
        apps.map((app) => (app.applicationId === selectedApplicationId ? { ...app, tenureStatus: true } : app)),
      )

      setEmploymentForm({
        employeeId: "",
        positionName: "",
        salary: "",
        commission: "",
        commissionPaid: "",
      })
      setShowEmploymentModal(false)
      setSelectedApplicationId("")
    } catch (error) {
      console.error("Failed to approve tenure with employment details:", error)
    }
  }

  const handleClaimClick = (applicationId: string) => {
    setClaimingAppId(applicationId)
    setShowClaimModal(true)
    setClaimComment("")
  }

  const handleSubmitClaim = async () => {
    try {
      await updateClaimStatus(claimingAppId, {
        isClaimed: true,
        comments: claimComment,
      })
      setApplications((apps) =>
        apps.map((app) => {
          if (app.applicationId === claimingAppId) {
            const commPayable = Number(app.commissionPayable) || 0
            return {
              ...app,
              isClaimed: true,
              commClaimed: commPayable,
            }
          }
          return app
        }),
      )
      setShowClaimModal(false)
      setClaimingAppId("")
      setClaimComment("")
      toast.success("Claimed successfully!")
    } catch (error) {
      toast.error("Failed to update claim status")
    }
  }

  const handleUnclaim = async (applicationId: string) => {
    try {
      await updateClaimStatus(applicationId, {
        isClaimed: false,
        comments: "",
      })
      setApplications((apps) =>
        apps.map((app) => {
          if (app.applicationId === applicationId) {
            return {
              ...app,
              isClaimed: false,
              commClaimed: 0,
            }
          }
          return app
        }),
      )
      toast.success("Unclaimed successfully!")
    } catch (error) {
      toast.error("Failed to update claim status")
    }
  }

  // Function to clear all filters to their default values
  
  const clearFilters = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setSelectedJob("all");
    setSelectedRecruiter("all");
    setSelectedDateFrom("");
    setSelectedDateTo("");
    setSelectedVerificationStatus("all");
    setSelectedTenureStatus("all");
    setSelectedTenureEligibilityStatus("all");
    setSelectedClaimStatus("all");
  };

  // Update the getAppointmentsForExcel function to include filters
// ...existing code...
const getAppointmentsForExcel = async () => {
  try {
    let allApplications: any[] = [];
    let currentPage = 1;
    let totalPages = 1;

    while (currentPage <= totalPages) {
      const response = await getJoinedApplications({
        page: currentPage,
        limit: 100,
        search: searchTerm,
        jobId: selectedJob !== "all" ? selectedJob : undefined,
        recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
        verificationStatus: selectedVerificationStatus !== "all" ? selectedVerificationStatus : undefined,
        tenureStatus: selectedTenureStatus !== "all" ? selectedTenureStatus : undefined,
        tenureEligibilityStatus: selectedTenureEligibilityStatus !== "all" ? selectedTenureEligibilityStatus : undefined,
        claimStatus: selectedClaimStatus !== "all" ? selectedClaimStatus : undefined,
        joiningDateFrom: selectedDateFrom || undefined,
        joiningDateTo: selectedDateTo || undefined,
        _t: Date.now(),
      });

      const apps = (response.data.data.applications ?? []).map((app: any) => ({
        "Joining Date": app.joiningDate ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A",
        "Candidate Name": app.candidateName || "N/A",
        "Email": app.email || "N/A",
        "Company Name": app.companyName || "N/A",
        "Company Location": app.companyLocation || "N/A",
        "Employee ID": app.employeeId || "N/A",
        "Applied By": app.ledBy?.username || "N/A",
        "Position": app.position || "N/A",
        "Phone": app.phone || "N/A",
        "Salary": app.salary || "N/A",
        "Commission Payable": app.commissionPayable || "N/A",
        "Commission Claimed": app.isCommissionClaimed ? "Yes" : "No",
        "Tenure Status": app.tenure?.status || "Pending",
        "Tenure Days Left": app.tenure?.daysLeft ?? "N/A",
        "Verification Status": app.verificationStatus || "Unknown",
        "Eligibility Status": app.tenureEligibilityStatus || "Unknown",
        "City": app.companyLocation || "N/A",
      }));

      allApplications = [...allApplications, ...apps];
      totalPages = response.data.data.totalPages || 1;
      currentPage += 1;
    }

    return allApplications;
  } catch (err: any) {
    console.error("Failed to fetch applications for Excel:", err);
    toast.error(err.response?.data?.message || "Failed to fetch applications for export", {
      duration: 4000,
    });
    return [];
  }
};
// ...existing code...
    const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
      column,
      children,
      className,
    }) => (
      <th
        className={cn(
          "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10",
          className
        )}
      >
        <div className="flex items-center gap-1">
          {children}
        </div>
      </th>
    )

  return (
<div>
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex">
        <div>
          <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="w-full">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate"> Tenure Candidates</h1>
                <p className="text-xs sm:text-sm text-gray-500">View and manage candidates with Tenure status</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search candidates..."
                  value={searchInputValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 w-full sm:w-64 border-gray-300 h-10 rounded-lg"
                />
              </div>
              <ExcelDownloadButton getData={getAppointmentsForExcel} filename="tenure-candidates-report" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <SearchableSelect
              options={jobOptions}
              value={selectedJob}
              onValueChange={(v) => {
                setSelectedJob(v)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              placeholder={isLoadingJobs ? "Loading jobs..." : "All Jobs"}
              emptyText="No jobs found"
              searchPlaceholder="Search jobs..."
              className="w-full max-w-xs h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<Building className="h-4 w-4 text-gray-500" />}
              searchable
            />
            <SearchableSelect
              options={recruiterOptions}
              value={selectedRecruiter}
              onValueChange={(v) => {
                setSelectedRecruiter(v)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              placeholder={isLoadingRecruiters ? "Loading..." : "All Recruiters"}
              emptyText="No recruiters found"
              searchPlaceholder="Search by username..."
              className="w-full max-w-xs h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<User className="h-4 w-4 text-gray-500" />}
              searchable
            />
            
            {/* <SearchableSelect
              options={tenureStatusOptions}
              value={selectedTenureStatus}
              onValueChange={(v) => {
                setSelectedTenureStatus(v)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              placeholder="All Tenure Status"
              emptyText="No status found"
              searchPlaceholder="Search status..."
              className="w-full max-w-xs h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<Award className="h-4 w-4 text-gray-500" />}
              // searchable
            /> */}
          <Select
            value={selectedTenureStatus}
            onValueChange={(v ) => {
                setSelectedTenureStatus(v)
                setPagination((p) => ({ ...p, page: 1 }))
            }}
            >
              <SelectTrigger className="w-full min-w-[150px] h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                <SelectValue placeholder="All Tenure Status" />
              </SelectTrigger>
              <SelectContent>
                {
                  tenureStatusOptions.map((claimStatus) => (
                    <SelectItem key={claimStatus.value} value={claimStatus.value}>{claimStatus.label}</SelectItem>
                  ))
                }
              </SelectContent>
          </Select>
            {/* <SearchableSelect
              options={tenureEligibilityStatusOptions}
              value={selectedTenureEligibilityStatus}
              onValueChange={(v) => {
                setSelectedTenureEligibilityStatus(v)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              placeholder="All Eligibility Status"
              emptyText="No status found"
              searchPlaceholder="Search status..."
              className="w-full max-w-xs h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<UserCheck className="h-4 w-4 text-gray-500" />}
              // searchable
            /> */}
          <Select
            value={selectedTenureEligibilityStatus}
            onValueChange={(v ) => {
                setSelectedTenureEligibilityStatus(v)
                setPagination((p) => ({ ...p, page: 1 }))
            }}
            >
              <SelectTrigger className="w-full min-w-[150px] h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                <SelectValue placeholder="All Eligibility Status" />
              </SelectTrigger>
              <SelectContent>
                {
                  tenureEligibilityStatusOptions.map((claimStatus) => (
                    <SelectItem key={claimStatus.value} value={claimStatus.value}>{claimStatus.label}</SelectItem>
                  ))
                }
              </SelectContent>
          </Select>
            {/* <SearchableSelect
              options={claimStatusOptions}
              value={selectedClaimStatus}
              onValueChange={(v) => {
                setSelectedClaimStatus(v)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              placeholder="All Claim Status"
              emptyText="No status found"
              searchPlaceholder="Search status..."
              className="w-full max-w-xs h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<CreditCard className="h-4 w-4 text-gray-500" />}
              // searchable
            /> */}
            <Select
              value={selectedClaimStatus}
              onValueChange={(event ) => {
                  const value = event;

                setSelectedClaimStatus(value)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              // className="w-full max-w-xs h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
            >
              <SelectTrigger className="w-full min-w-[150px] h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                {
                  claimStatusOptions.map((claimStatus) => (
                    <SelectItem key={claimStatus.value} value={claimStatus.value}>{claimStatus.label}</SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            <div className="flex gap-2">
            <Input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => {
                setSelectedDateFrom(e.target.value)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              max={selectedDateTo || undefined}
              className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              title="From Date"
            />
            <Input
              type="date"
              value={selectedDateTo}
              onChange={(e) => {
                setSelectedDateTo(e.target.value)
                setPagination((p) => ({ ...p, page: 1 }))
              }}
              min={selectedDateFrom || undefined}
              className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              title="To Date"
            />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="h-10 rounded-lg bg-[#EC6E51] hover:bg-orange-50 text-white"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showCandidateDetailsModal} onOpenChange={setShowCandidateDetailsModal}>
        <DialogContent className="max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl no-scrollbar mx-4 sm:mx-auto">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                {selectedCandidateForDetails?.name.charAt(0).toUpperCase()}
              </div>
              <span className="break-words">{selectedCandidateForDetails?.name}</span>
            </DialogTitle>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="text-center py-8 sm:py-12">
              <div className="animate-spin inline-block h-8 w-8 sm:h-10 sm:w-10 border-4 border-orange-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-base sm:text-lg text-gray-600">Loading candidate details...</p>
            </div>
          ) : detailsError ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-base sm:text-lg text-red-600">{detailsError}</p>
            </div>
          ) : selectedCandidateForDetails ? (
            <div className="space-y-4 sm:space-y-6 p-0">
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                          {selectedCandidateForDetails.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.phone}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Father's Name</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.fatherName || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <UserCheck className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Gender</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.gender || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Location</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.currentLocationDetails?.city || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Baby className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Date of Birth</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.dateOfBirth
                            ? new Date(selectedCandidateForDetails.dateOfBirth).toLocaleDateString("en-GB")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CreditCard className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Aadhar Number</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.aadharNumber || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Highest Qualification</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.highestQualification || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Languages className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Spoken Languages</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.spokenLanguages?.length
                            ? selectedCandidateForDetails.spokenLanguages.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Experience</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.totalExperienceYears !== undefined &&
                          selectedCandidateForDetails.totalExperienceMonths !== undefined
                            ? `${selectedCandidateForDetails.totalExperienceYears} years ${selectedCandidateForDetails.totalExperienceMonths} months`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Award className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Skills</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedCandidateForDetails.skills?.length ? (
                            selectedCandidateForDetails.skills.slice(0, 3).map((skill, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))
                          ) : (
                            <span className="font-medium text-gray-900 text-sm sm:text-base">N/A</span>
                          )}
                          {selectedCandidateForDetails.skills?.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{selectedCandidateForDetails.skills.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <Building className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Job Category</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.preferredJobCategories?.length
                            ? selectedCandidateForDetails.preferredJobCategories.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">Employment Type</p>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">
                          {selectedCandidateForDetails.preferredEmploymentTypes?.length
                            ? selectedCandidateForDetails.preferredEmploymentTypes.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
             
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={showEmploymentModal} onOpenChange={setShowEmploymentModal}>
        <DialogContent className="sm:max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Update Employment Details</DialogTitle>
            <DialogDescription className="text-gray-600">Enter the employment details for the candidate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Employee ID <span className="text-red-500">*</span></label>
              <Input
                type="text"
                placeholder="Enter Employee ID"
                value={employmentForm.employeeId}
                onChange={(e) => setEmploymentForm((prev) => ({ ...prev, employeeId: e.target.value }))}
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Position Name <span className="text-red-500">*</span></label>
              <Input
                type="text"
                placeholder="Enter Position Name"
                value={employmentForm.positionName}
                onChange={(e) => setEmploymentForm((prev) => ({ ...prev, positionName: e.target.value }))}
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Salary <span className="text-red-500">*</span></label>
              <Input
                type="text"
                placeholder="Enter Salary"
                value={employmentForm.salary}
                onChange={(e) => setEmploymentForm((prev) => ({ ...prev, salary: e.target.value }))}
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Commission Receivable <span className="text-red-500">*</span></label>
              <Input
                type="text"
                placeholder="Enter Commission Receivable"
                value={employmentForm.commission}
                onChange={(e) => setEmploymentForm((prev) => ({ ...prev, commission: e.target.value }))}
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Commission Paid <span className="text-red-500">*</span></label>
              <Input
                type="text"
                placeholder="Enter Commission Paid"
                value={employmentForm.commissionPaid}
                onChange={(e) => setEmploymentForm((prev) => ({ ...prev, commissionPaid: e.target.value }))}
                className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowEmploymentModal(false)}
                className="rounded-xl border-gray-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmploymentSubmit}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700"
              >
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showClaimModal} onOpenChange={setShowClaimModal}>
        <DialogContent className="sm:max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Add Comment for Claim</DialogTitle>
            <DialogDescription className="text-gray-600">Provide a comment for claiming the commission.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Comment <span className="text-red-500">*</span></label>
              <Textarea
                placeholder="Enter your comment..."
                value={claimComment}
                onChange={(e) => setClaimComment(e.target.value)}
                className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowClaimModal(false)}
                className="rounded-xl border-gray-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitClaim}
                disabled={!claimComment.trim()}
                className="rounded-xl bg-orange-600 text-white hover:bg-orange-700"
              >
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-background relative">
        <div className="h-[calc(100vh-275px)] overflow-auto">
          <table className="min-w-full divide-y divide-border">
            <thead style={{ backgroundColor: "#FFF5F2" }}>
              <tr>
                <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="candidate.name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                  Candidate Name
                </SortableHeader>
                <SortableHeader column="job.companyName" className="min-w-40">Company Name</SortableHeader>
                <SortableHeader column="candidate.currentLocationDetails.city" className="min-w-32">Location</SortableHeader>
                <SortableHeader column="phone" className="min-w-32">Phone</SortableHeader>
                <SortableHeader column="email" className="min-w-64">Email</SortableHeader>
                <SortableHeader column="joiningDate" className="min-w-32">Joined Date</SortableHeader>
                <SortableHeader column="ledBy.username" className="min-w-32">Led By</SortableHeader>
                <SortableHeader column="employeeId" className="min-w-32">Employee ID</SortableHeader>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-48 sticky top-0 bg-[#FFF5F2] z-10">
                  Tenure (Days Left)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Eligibility Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Comm. Payable
                </th>
                {/* {
                  isAdmin && (
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Comm. Claimed
                </th>
                    
                  )
                } */}
               
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {isLoading ? (
                <>
                  {[...Array(10)].map((_, index) => (
                    <tr key={`shimmer-${index}`} className="bg-white divide-y divide-border">
                      {/* Empty column */}
                      <td className="px-4 py-4"></td>
                      {/* Candidate Name */}
                      <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      {/* Company Name */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-40 h-4"></div>
                      </td>
                      {/* Location */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      {/* Phone */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Email */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                      </td>
                      {/* Joined Date */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                      </td>
                      {/* Led By */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Employee ID */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                      {/* Tenure Days */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Eligibility Status */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded-full w-24 h-8"></div>
                      </td>
                      {/* Commission Payable */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">{error}</p>
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">No candidates found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr
                    key={app.applicationId}
                    className={cn("hover:bg-muted/30 transition-colors cursor-pointer")}
                  >
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border relative group">
                      <div className="flex items-center gap-3">
                        <span className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary">
                          {app.candidateName ?? "N/A"}
                        </span>
                      </div>
                      <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-lg">{app.candidateName ?? "N/A"}</h4>
                          
                        </div>
                        <div className="space-y-2 text-sm">
                        
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Location:</span>
                            <div className="max-w-48 truncate" title={app.companyLocation}>
                              {app.companyLocation || "N/A"}
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Phone:</span>
                            <span className="font-medium">{app.phone || "N/A"}</span>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <div className="text-xs text-muted-foreground">
                            Joined {app.joiningDate ? new Date(app.joiningDate).toLocaleDateString() : "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">{app.companyName || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground px-4 py-4 text-sm text-foreground truncate max-w-48">{app?.companyLocation || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(app.phone || "")
                          toast.success("Phone number copied!")
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none"
                        }}
                      >
                        {app.phone || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy email"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(app.email || "")
                          toast.success("Email copied!")
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none"
                        }}
                      >
                        {app.email || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {app.joiningDate ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">{app.ledBy?.username || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground">{app.employeeId || "N/A"}</td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {app.tenure?.daysLeft !== undefined && app.tenure?.daysLeft !== null ? `${app.tenure.daysLeft} Days Left` : "N/A"} 
                      {/* <Badge className="text-xs">{app.tenure?.status || "N/A"}</Badge> */}
                    </td>
                    <td className="px-4 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg px-3 py-1.5 border border-orange-300 text-orange-600 font-medium bg-white shadow-sm hover:bg-orange-50"
                            disabled={
                              (app.tenure?.daysLeft !== undefined && app.tenure.daysLeft !==null) ||
                              app.tenure?.status?.toLowerCase() === "approved" ||
                              app.tenure?.status?.toLowerCase() === "not approved"
                            }
                          >
                            {app.tenure.status || "Unknown"} 
                          </Button>
                        </DropdownMenuTrigger>
                        {!((app.tenure?.daysLeft !== undefined && app.tenure.daysLeft !==null)) && (
                          <DropdownMenuContent className="rounded-xl shadow-xl border-0 bg-white/95 backdrop-blur-sm">
                            <DropdownMenuItem
                              onClick={() => handleUpdateTenureEligibilityStatus(app.applicationId, TenureEligibilityStatus.UNKNOWN)}
                              className="cursor-pointer hover:bg-gray-50 px-4 py-2 text-gray-700"
                            >
                              Unknown
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateTenureEligibilityStatus(app.applicationId, TenureEligibilityStatus.ELIGIBLE)}
                              className="cursor-pointer hover:bg-green-50 px-4 py-2 text-green-700"
                            >
                              Eligible
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateTenureEligibilityStatus(app.applicationId, TenureEligibilityStatus.NOT_ELIGIBLE)}
                              className="cursor-pointer hover:bg-red-50 px-4 py-2 text-red-700"
                            >
                              Not Eligible
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        )}
                      </DropdownMenu>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">{app.commissionPayable || "N/A"}</td>
                  
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 justify-end p-4 pl-8">
          <div className="text-sm text-muted-foreground">
            Showing {applications.length} of {pagination.totalResults} candidates
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(
                (pageNum) =>
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
              )
              .map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading}
                  className={
                    pagination.page === pageNum
                      ? "bg-orange-600 text-white hover:bg-orange-700"
                      : "border-gray-200 hover:bg-orange-50"
                  }
                >
                  {pageNum}
                </Button>
              ))}
            {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
              <span className="text-sm text-muted-foreground">...</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewTenureApplications